// Marketing Brief screen.
//
// Two modes:
//   input  → brand picker + list of existing briefs (cards). New briefs are
//            generated via the Wizard (which feeds the workflow), so this
//            screen is primarily a *viewer* for already-saved briefs.
//   result → V02 "Document" layout: sticky left TOC + 10 long-form sections.
//
// Deep-link via #/marketing-brief?brand=<slug>&brief=<slug>.

import { useEffect, useRef, useState } from 'react';
import { PALETTE } from '../palette.js';
import { Card, Pill, Button } from '../components/primitives.jsx';
import ProtectedPageLoader from '../components/ProtectedPageLoader.jsx';
import { api } from '../api.js';

function activeCompanyId(session) {
  const memberships = session?.user?.memberships || [];
  return session?.user?.activeCompany?.id || memberships[0]?.companyId || '';
}

function tenantHeaders(companyId) {
  return companyId ? { headers: { 'x-company-id': companyId } } : undefined;
}

export function normalizeSaasMarketingBrief(brief = {}) {
  return {
    ...(brief.content || {}),
    _slug: brief.briefSlug || brief.id || '',
    _brandName: brief.brand?.name || '',
    _generatedAt: brief.createdAt
      ? new Date(brief.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
      : '',
    _saasBrief: {
      id: brief.id,
      brand: brief.brand || null,
      campaign: brief.campaign || null,
      workflowJobId: brief.workflowJobId || null,
      reviewRoute: brief.reviewRoute || null,
    },
  };
}

export function safeMarketingBriefError(error, fallback = 'Marketing briefs could not be loaded. Try again.') {
  const code = String(error?.code || '').toLowerCase();
  if (code === 'marketing_brief_not_found') return 'That marketing brief is not available in the active company.';
  if (code === 'marketing_brief_content_missing') return 'This brief record exists, but its document is not available yet.';
  if (code === 'marketing_brief_content_invalid' || code === 'marketing_brief_content_incomplete') {
    return 'This brief could not be opened because its document is incomplete.';
  }
  if (Number(error?.status) === 403) return 'Your company role cannot open marketing briefs.';
  return fallback;
}

export default function MarketingBrief({ navigate, route, session }) {
  const [mode, setMode] = useState('input');
  const [brands, setBrands] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [briefs, setBriefs] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [data, setData] = useState(null);
  const [briefContext, setBriefContext] = useState(null);
  const [error, setError] = useState('');
  const companyId = activeCompanyId(session);
  const isSaasSession = session?.user?.auth?.type === 'supabase';

  // Load brands.
  useEffect(() => {
    let active = true;
    setBrands(null);
    setSelectedBrand('');
    setBriefs(null);
    setPagination(null);
    setError('');
    const request = isSaasSession
      ? api.get('/api/saas/brands', tenantHeaders(companyId)).then((response) => response.brands || [])
      : api.get('/api/clients').then((response) => response.clients || []);
    request
      .then((nextBrands) => { if (active) setBrands(nextBrands); })
      .catch((err) => {
        if (!active) return;
        setBrands([]);
        setError(safeMarketingBriefError(err));
      });
    return () => { active = false; };
  }, [isSaasSession, companyId]);

  // When brand changes, load its briefs.
  useEffect(() => {
    let active = true;
    if (!selectedBrand) {
      setBriefs(null);
      setPagination(null);
      return () => { active = false; };
    }
    setBriefs(null);
    setPagination(null);
    setError('');
    const request = isSaasSession
      ? api.get(
        `/api/saas/marketing-briefs?brandId=${encodeURIComponent(selectedBrand)}&limit=50&offset=0`,
        tenantHeaders(companyId),
      )
      : api.get(`/api/briefs/${encodeURIComponent(selectedBrand)}`);
    request
      .then((response) => {
        if (!active) return;
        setBriefs(response.briefs || []);
        setPagination(response.pagination || null);
      })
      .catch((err) => {
        if (!active) return;
        setBriefs([]);
        setError(safeMarketingBriefError(err));
      });
    return () => { active = false; };
  }, [selectedBrand, isSaasSession, companyId]);

  // Deep-link: ?brand=&brief= → load saved brief.
  useEffect(() => {
    const brand = route?.query?.brand;
    const brief = route?.query?.brief;
    if (!brief || (!isSaasSession && !brand)) return;
    let active = true;
    setError('');
    setMode('input');
    setData(null);
    setBriefContext(null);
    setDetailLoading(true);
    const request = isSaasSession
      ? api.get(`/api/saas/marketing-briefs/${encodeURIComponent(brief)}`, tenantHeaders(companyId))
      : api.get(`/api/library/${encodeURIComponent(brand)}/brief/${encodeURIComponent(brief)}`);
    request
      .then((r) => {
        if (!active) return;
        if (isSaasSession) {
          const normalized = normalizeSaasMarketingBrief(r.brief || {});
          setData(normalized);
          setBriefContext(normalized._saasBrief);
          setSelectedBrand(r.brief?.brand?.id || '');
        } else {
          setData(r.data || r);
        }
        setMode('result');
      })
      .catch((err) => {
        if (active) setError(safeMarketingBriefError(err, 'The marketing brief could not be opened. Try again.'));
      })
      .finally(() => { if (active) setDetailLoading(false); });
    return () => { active = false; };
  }, [route?.query?.brand, route?.query?.brief, isSaasSession, companyId]);

  async function loadMore() {
    if (!isSaasSession || !selectedBrand || !pagination?.hasMore || loadingMore) return;
    setLoadingMore(true);
    setError('');
    try {
      const response = await api.get(
        `/api/saas/marketing-briefs?brandId=${encodeURIComponent(selectedBrand)}&limit=50&offset=${pagination.nextOffset}`,
        tenantHeaders(companyId),
      );
      setBriefs((current) => [...new Map([...(current || []), ...(response.briefs || [])]
        .map((brief) => [brief.id, brief])).values()]);
      setPagination(response.pagination || null);
    } catch (err) {
      setError(safeMarketingBriefError(err));
    } finally {
      setLoadingMore(false);
    }
  }

  function reset() {
    setMode('input');
    setData(null);
    setBriefContext(null);
    setError('');
    if (route?.query?.brand || route?.query?.brief) navigate('marketing-brief');
  }

  if (mode === 'result' && data) {
    return <DocView data={data} context={briefContext} onReset={reset} navigate={navigate} />;
  }

  if (isSaasSession && (brands == null || detailLoading)) {
    return (
      <div className="marketing-brief-page" style={{ padding: '32px 40px 64px', maxWidth: 1100, margin: '0 auto' }}>
        <style>{marketingBriefResponsiveStyles}</style>
        <ProtectedPageLoader
          title="Loading marketing briefs"
          body="Checking your company access and tenant-owned campaign documents."
          minHeight={360}
        />
      </div>
    );
  }

  return (
    <div className="marketing-brief-page" style={{ padding: '32px 40px 64px', maxWidth: 1100, margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>
      <style>{marketingBriefResponsiveStyles}</style>
      <header className="marketing-brief-page-header" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <h1 style={{
          fontFamily: 'Instrument Serif, serif', fontStyle: 'italic',
          fontSize: 40, fontWeight: 400, margin: 0, color: PALETTE.ink, letterSpacing: '-0.02em',
        }}>Marketing brief</h1>
        <Pill tone="accent">Brief</Pill>
        <div style={{ flex: 1 }} />
        <Button variant="primary" size="sm" onClick={() => navigate('wizard')}>＋ New brief</Button>
      </header>

      {error && (
        <div style={{
          background: PALETTE.dangerSoft, border: '1px solid #E8C9C1', color: '#8B2E1A',
          borderRadius: 12, padding: '12px 16px', fontSize: 13, marginBottom: 20,
        }}>{error}</div>
      )}

      <Card style={{ padding: 24, marginBottom: 24 }}>
        <Eyebrow style={{ marginBottom: 12 }}>Pick a brand</Eyebrow>
        {brands == null ? (
          <div style={{ color: PALETTE.ink3, fontSize: 13, fontStyle: 'italic' }}>Loading…</div>
        ) : brands.length === 0 ? (
          <div style={{ color: PALETTE.ink3, fontSize: 13 }}>
            No brands yet. <a href="#/brand-research" style={{ color: PALETTE.accent }}>Run brand research first.</a>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
            {brands.map((b) => {
              const brandKey = isSaasSession ? b.id : b.slug;
              return (
              <button key={brandKey} onClick={() => setSelectedBrand(brandKey)} style={{
                display: 'flex', gap: 10, alignItems: 'center',
                padding: '12px 14px',
                background: selectedBrand === brandKey ? PALETTE.accentTint : PALETTE.surface,
                border: `1px solid ${selectedBrand === brandKey ? PALETTE.accentSoft : PALETTE.line}`,
                borderRadius: 12, cursor: 'pointer', textAlign: 'left',
                fontFamily: 'Inter, sans-serif',
              }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8, background: PALETTE.surfaceAlt,
                  border: `1px solid ${PALETTE.line}`, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontWeight: 700, color: PALETTE.accent, flexShrink: 0,
                }}>{(b.name || '?').trim().charAt(0).toUpperCase()}</div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: PALETTE.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.name}</div>
                  <div style={{ fontSize: 11, color: PALETTE.ink3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.location || b.websiteUrl || b.website || ''}</div>
                </div>
              </button>
              );
            })}
          </div>
        )}
      </Card>

      {selectedBrand && (
        <div>
          <Eyebrow style={{ marginBottom: 12 }}>Briefs for this brand</Eyebrow>
          {briefs == null ? (
            <div style={{ color: PALETTE.ink3, fontSize: 13, fontStyle: 'italic' }}>Loading…</div>
          ) : briefs.length === 0 ? (
            <Card style={{ padding: 24, textAlign: 'center' }}>
              <div style={{ fontSize: 14, color: PALETTE.ink2, marginBottom: 12 }}>
                No briefs for this brand yet.
              </div>
              <Button variant="accent" onClick={() => navigate('wizard')}>Build the first brief →</Button>
            </Card>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
              {briefs.map((b) => (
                <a key={b.id || b.slug} href={isSaasSession
                  ? `#/marketing-brief?brief=${encodeURIComponent(b.id)}`
                  : `#/marketing-brief?brand=${encodeURIComponent(selectedBrand)}&brief=${encodeURIComponent(b.slug)}`} style={{
                  display: 'block', padding: '14px 16px',
                  background: PALETTE.surface, border: `1px solid ${PALETTE.line}`,
                  borderRadius: 12, textDecoration: 'none', color: 'inherit',
                }}>
                  <Eyebrow style={{ fontSize: 9.5, marginBottom: 6 }}>{b.campaign?.offer || b.offer || 'Brief'}</Eyebrow>
                  <div style={{
                    fontFamily: 'Instrument Serif, serif', fontSize: 18, fontWeight: 400,
                    color: PALETTE.ink, lineHeight: 1.25, marginBottom: 4,
                  }}>
                    {b.title || b.campaignName || b.slug}
                  </div>
                  {(b.createdAt || b.updatedAt) && (
                    <div style={{ fontSize: 11, color: PALETTE.ink3 }}>
                      Updated {new Date(b.createdAt || b.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                  )}
                </a>
              ))}
            </div>
          )}
          {isSaasSession && pagination?.hasMore && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
              <Button variant="soft" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? 'Loading…' : 'Load more briefs'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── V02 DOC VIEW ─────────────────────────────────────────────────────────
const marketingBriefResponsiveStyles = `
  .marketing-brief-mobile-nav { display: none; }
  @media (max-width: 800px) {
    .marketing-brief-page { padding: 24px 18px 48px !important; }
    .marketing-brief-page-header { align-items: flex-start !important; flex-wrap: wrap; }
    .marketing-brief-page-header > button { width: 100%; justify-content: center; }
    .marketing-brief-document { display: block !important; height: auto !important; min-height: 0; overflow: visible !important; }
    .marketing-brief-document-aside { display: none !important; }
    .marketing-brief-document-body { overflow: visible !important; }
    .marketing-brief-mobile-nav {
      position: sticky;
      top: 0;
      z-index: 5;
      display: grid;
      grid-template-columns: auto minmax(0, 1fr) auto;
      gap: 8px;
      align-items: center;
      padding: 10px 14px;
      border-bottom: 1px solid ${PALETTE.line};
      background: ${PALETTE.surfaceAlt};
    }
    .marketing-brief-mobile-nav button,
    .marketing-brief-mobile-nav select { min-width: 0; height: 38px; }
    .marketing-brief-mobile-nav select {
      width: 100%;
      border: 1px solid ${PALETTE.line};
      border-radius: 7px;
      background: ${PALETTE.surface};
      color: ${PALETTE.ink};
      padding: 0 10px;
      font: inherit;
    }
    .marketing-brief-document-header,
    .marketing-brief-document-content { padding-left: 18px !important; padding-right: 18px !important; }
    .marketing-brief-document-title { font-size: 34px !important; overflow-wrap: anywhere; }
    .marketing-brief-two-column { grid-template-columns: minmax(0, 1fr) !important; }
    .marketing-brief-funnel-row { grid-template-columns: 72px 18px minmax(0, 1fr) !important; gap: 8px !important; }
    .marketing-brief-outline-row { grid-template-columns: minmax(0, 1fr) !important; }
    .marketing-brief-outline-row > :last-child { text-align: left !important; white-space: normal !important; }
    .marketing-brief-payment-row { grid-template-columns: minmax(0, 1fr) !important; gap: 4px !important; }
    .marketing-brief-assumption-row { grid-template-columns: 24px minmax(0, 1fr) !important; }
    .marketing-brief-assumption-row > button { grid-column: 2; justify-self: start; }
    .marketing-brief-kv { grid-template-columns: minmax(0, 1fr) !important; gap: 4px !important; }
  }
  @media (max-width: 430px) {
    .marketing-brief-mobile-nav { grid-template-columns: 1fr 1fr; }
    .marketing-brief-mobile-nav select { grid-column: 1 / -1; grid-row: 1; }
  }
`;

const TOC = [
  { id: 'overview',     label: 'Overview' },
  { id: 'offer',        label: 'Offer' },
  { id: 'funnel',       label: 'Lead funnel' },
  { id: 'landing',      label: 'Landing page' },
  { id: 'ads',          label: 'Meta ads' },
  { id: 'email',        label: 'Email' },
  { id: 'metrics',      label: 'Metrics' },
  { id: 'deliverables', label: 'Deliverables' },
  { id: 'voice',        label: 'Voice & compliance' },
  { id: 'assumptions',  label: 'Assumptions' },
];

export function DocView({ data, context, onReset, navigate }) {
  const sectionRefs = useRef({});
  const scrollerRef = useRef(null);
  const mobileNavigationTargetRef = useRef(null);
  const mobileNavigationTimerRef = useRef(null);
  const [active, setActive] = useState('overview');

  useEffect(() => {
    const sc = scrollerRef.current;
    if (!sc) return;
    const isMobile = typeof window !== 'undefined'
      && window.matchMedia?.('(max-width: 800px)').matches;
    const onScroll = () => {
      if (isMobile && mobileNavigationTargetRef.current) {
        setActive(mobileNavigationTargetRef.current);
        return;
      }
      let cur = TOC[0].id;
      const mobileNav = sc.parentElement?.querySelector('.marketing-brief-mobile-nav');
      const mobileTop = (mobileNav?.getBoundingClientRect().bottom || 96) + 24;
      for (const t of TOC) {
        const el = sectionRefs.current[t.id];
        if (!el) continue;
        const reached = isMobile
          ? el.getBoundingClientRect().top <= mobileTop
          : el.offsetTop <= sc.scrollTop + 120;
        if (reached) cur = t.id;
      }
      setActive(cur);
    };
    if (isMobile) {
      document.addEventListener('scroll', onScroll, true);
      window.addEventListener('scroll', onScroll, { passive: true });
    } else {
      sc.addEventListener('scroll', onScroll, { passive: true });
    }
    onScroll();
    return () => {
      if (mobileNavigationTimerRef.current) clearTimeout(mobileNavigationTimerRef.current);
      if (isMobile) {
        document.removeEventListener('scroll', onScroll, true);
        window.removeEventListener('scroll', onScroll);
      } else {
        sc.removeEventListener('scroll', onScroll);
      }
    };
  }, []);

  function goTo(id) {
    const el = sectionRefs.current[id];
    const sc = scrollerRef.current;
    if (!el || !sc) return;
    setActive(id);
    if (typeof window !== 'undefined' && window.matchMedia?.('(max-width: 800px)').matches) {
      mobileNavigationTargetRef.current = id;
      if (mobileNavigationTimerRef.current) clearTimeout(mobileNavigationTimerRef.current);
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      mobileNavigationTimerRef.current = setTimeout(() => {
        mobileNavigationTargetRef.current = null;
      }, 700);
      return;
    }
    sc.scrollTo({ top: el.offsetTop - 24, behavior: 'smooth' });
  }

  const nextRoute = context?.reviewRoute || 'funnels';
  const nextLabel = context?.reviewRoute ? 'Review' : 'Build funnel';

  function downloadJson() {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${data._slug || 'marketing-brief'}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  }

  const angleCount = data.metaAds?.creatives?.length || 0;
  const lpCount = data.landingPage?.sections?.length || 0;
  const emailCount = (data.email?.announcement?.length || 0) + (data.email?.nurture?.length || 0);
  const generatedAt = data._generatedAt
    || (data.createdAt && new Date(data.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }))
    || new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <div className="marketing-brief-document" style={{ height: '100vh', display: 'flex', overflow: 'hidden', background: PALETTE.bg, fontFamily: 'Inter, sans-serif' }}>
      <style>{marketingBriefResponsiveStyles}</style>
      <div className="marketing-brief-mobile-nav" aria-label="Marketing brief navigation">
        <button onClick={onReset} style={mobileNavButtonStyle}>Back</button>
        <select aria-label="Section" value={active} onChange={(event) => goTo(event.target.value)}>
          {TOC.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
        </select>
        <button onClick={() => navigate(nextRoute)} style={mobileNavPrimaryButtonStyle}>{nextLabel}</button>
      </div>
      {/* Sticky TOC */}
      <aside className="marketing-brief-document-aside" style={{
        width: 220, flexShrink: 0,
        borderRight: `1px solid ${PALETTE.line}`,
        background: PALETTE.surfaceAlt,
        padding: '24px 16px',
        overflowY: 'auto',
        display: 'flex', flexDirection: 'column', gap: 2,
      }}>
        <Eyebrow style={{ color: PALETTE.ink3, marginBottom: 14, padding: '0 8px' }}>On this page</Eyebrow>
        {TOC.map((t, i) => (
          <button key={t.id} onClick={() => goTo(t.id)} style={{
            display: 'flex', alignItems: 'center', gap: 10, width: '100%',
            padding: '7px 10px', borderRadius: 7,
            background: active === t.id ? PALETTE.surface : 'transparent',
            border: active === t.id ? `1px solid ${PALETTE.line}` : '1px solid transparent',
            color: active === t.id ? PALETTE.ink : PALETTE.ink2,
            fontSize: 12.5, fontWeight: active === t.id ? 500 : 400,
            fontFamily: 'inherit', cursor: 'pointer', textAlign: 'left',
          }}>
            <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: PALETTE.ink3, width: 18 }}>
              {String(i + 1).padStart(2, '0')}
            </span>
            <span>{t.label}</span>
          </button>
        ))}

        <div style={{
          marginTop: 18, padding: 12,
          background: PALETTE.surface, border: `1px solid ${PALETTE.line}`, borderRadius: 10,
        }}>
          <div style={{ fontSize: 11, color: PALETTE.ink3, marginBottom: 4, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Status</div>
          <div style={{ fontSize: 12, color: PALETTE.ink, lineHeight: 1.4 }}>Draft · ready to review</div>
          <div style={{ marginTop: 10, display: 'flex', gap: 6 }}>
            <button onClick={onReset} style={{
              flex: 1, padding: '7px 8px', fontSize: 11, borderRadius: 6,
              border: `1px solid ${PALETTE.line}`, background: PALETTE.surface, color: PALETTE.ink2, cursor: 'pointer',
            }}>Back</button>
            <button onClick={() => navigate(nextRoute)} style={{
              flex: 1, padding: '7px 8px', fontSize: 11, borderRadius: 6,
              border: `1px solid ${PALETTE.ink}`, background: PALETTE.ink, color: '#FAF8F5', cursor: 'pointer',
            }}>{nextLabel}</button>
          </div>
        </div>
      </aside>

      {/* Doc body */}
      <div ref={scrollerRef} className="marketing-brief-document-body" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {/* Header */}
        <div className="marketing-brief-document-header" style={{ maxWidth: 760, margin: '0 auto', padding: '44px 48px 24px' }}>
          <Eyebrow style={{ marginBottom: 12 }}>Marketing Brief · {generatedAt}</Eyebrow>
          <h1 className="marketing-brief-document-title" style={{
            margin: 0, fontFamily: 'Instrument Serif, serif',
            fontSize: 46, fontWeight: 400, color: PALETTE.ink, letterSpacing: '-0.015em',
            lineHeight: 1.05, marginBottom: 14,
          }}>{data.campaignName || 'Untitled brief'}</h1>
          <div style={{ fontSize: 14.5, color: PALETTE.ink2, lineHeight: 1.55, marginBottom: 18 }}>
            {data.overview?.summary}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {data._brandName && <Tag tone="accent">For: {data._brandName}</Tag>}
            <Tag tone="neutral">{angleCount} ad concepts · {lpCount ? `${lpCount} LP sections` : '1 LP'} · {emailCount} emails</Tag>
            <div style={{ flex: 1 }} />
            <button onClick={downloadJson} style={{
              padding: '7px 12px', fontSize: 12, borderRadius: 7,
              border: `1px solid ${PALETTE.line}`, background: PALETTE.surface, color: PALETTE.ink2,
              cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>↓ Export JSON</button>
          </div>
        </div>

        <div className="marketing-brief-document-content" style={{ maxWidth: 760, margin: '0 auto', padding: '12px 48px 80px' }}>

          {/* 01 OVERVIEW */}
          <Section n="01" id="overview" sectionRefs={sectionRefs}>
            <SectionTitle>Overview</SectionTitle>
            {data.overview?.coreTension && (
              <div style={{ background: PALETTE.surface, border: `1px solid ${PALETTE.line}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
                <Eyebrow style={{ marginBottom: 8 }}>The thesis</Eyebrow>
                <div style={{ fontSize: 18, lineHeight: 1.45, color: PALETTE.ink, fontFamily: 'Instrument Serif, serif' }}>
                  {data.overview.coreTension}
                </div>
              </div>
            )}
            {data.overview?.what && (
              <Para><strong style={{ color: PALETTE.ink }}>What it is.</strong> {data.overview.what}</Para>
            )}
            {data.overview?.whyItMatters && (
              <Para><strong style={{ color: PALETTE.ink }}>Why it matters.</strong> {data.overview.whyItMatters}</Para>
            )}
          </Section>

          {/* 02 OFFER */}
          {data.offer && (
            <Section n="02" id="offer" sectionRefs={sectionRefs}>
              <SectionTitle>Offer</SectionTitle>
              <div style={{ fontSize: 18, color: PALETTE.ink, fontFamily: 'Instrument Serif, serif', marginBottom: 18, lineHeight: 1.4 }}>
                "{data.offer.name}"
              </div>

              {data.offer.keyClaims?.length > 0 && (
                <>
                  <SubTitle>Key claims</SubTitle>
                  <ul style={{ margin: 0, padding: '0 0 0 20px', marginBottom: 22 }}>
                    {data.offer.keyClaims.map((c, i) => <Bullet key={i}>{c}</Bullet>)}
                  </ul>
                </>
              )}

              {data.offer.paymentAnchors?.length > 0 && (
                <>
                  <SubTitle>Price anchors</SubTitle>
                  <div style={{ border: `1px solid ${PALETTE.line}`, borderRadius: 10, overflow: 'hidden', background: PALETTE.surface, marginBottom: 22 }}>
                    {data.offer.paymentAnchors.map((p, i, arr) => (
                      <div key={i} className="marketing-brief-payment-row" style={{
                        display: 'grid', gridTemplateColumns: '1fr auto',
                        padding: '12px 16px', gap: 16,
                        borderBottom: i < arr.length - 1 ? `1px solid ${PALETTE.line}` : 'none',
                      }}>
                        <div style={{ fontSize: 13.5, color: PALETTE.ink, fontWeight: 500 }}>{p.renovationType}</div>
                        <div style={{ fontSize: 13, color: PALETTE.ink2, fontFamily: 'JetBrains Mono, monospace' }}>{p.terms}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {data.offer.eligibility?.length > 0 && (
                <>
                  <SubTitle>Eligibility</SubTitle>
                  <ul style={{ margin: 0, padding: '0 0 0 20px', marginBottom: 22 }}>
                    {data.offer.eligibility.map((c, i) => <Bullet key={i}>{c}</Bullet>)}
                  </ul>
                </>
              )}

              {data.offer.minimums?.length > 0 && (
                <>
                  <SubTitle>Minimums</SubTitle>
                  <ul style={{ margin: 0, padding: '0 0 0 20px' }}>
                    {data.offer.minimums.map((c, i) => <Bullet key={i}>{c}</Bullet>)}
                  </ul>
                </>
              )}
            </Section>
          )}

          {/* 03 FUNNEL */}
          {data.leadStrategy && (
            <Section n="03" id="funnel" sectionRefs={sectionRefs}>
              <SectionTitle>Lead funnel</SectionTitle>
              {data.leadStrategy.funnel?.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  {data.leadStrategy.funnel.map((f, i, arr) => (
                    <div key={i} className="marketing-brief-funnel-row" style={{
                      display: 'grid', gridTemplateColumns: '100px 22px 1fr', gap: 12,
                      paddingBottom: 16, position: 'relative',
                    }}>
                      <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: PALETTE.ink3, paddingTop: 4, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{f.stage}</div>
                      <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
                        <div style={{
                          width: 10, height: 10, marginTop: 5, borderRadius: 999,
                          background: PALETTE.accent, border: `2px solid ${PALETTE.surface}`,
                          boxShadow: `0 0 0 1px ${PALETTE.accentSoft}`, zIndex: 1,
                        }}/>
                        {i < arr.length - 1 && <div style={{ position: 'absolute', top: 14, bottom: -2, width: 1, background: PALETTE.line }}/>}
                      </div>
                      <div>
                        <div style={{ fontSize: 14, color: PALETTE.ink, fontWeight: 500, marginBottom: 2 }}>{f.step}</div>
                        <div style={{ fontSize: 13, color: PALETTE.ink2, lineHeight: 1.5 }}>{f.action}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {(data.leadStrategy.qualifiesIf?.length || data.leadStrategy.disqualifiesIf?.length) && (
                <div className="marketing-brief-two-column" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 22 }}>
                  <div style={{ background: PALETTE.successSoft, border: '1px solid #CFD9B4', borderRadius: 10, padding: 14 }}>
                    <Eyebrow color={PALETTE.success} style={{ marginBottom: 8 }}>Qualifies if</Eyebrow>
                    <ul style={{ margin: 0, padding: '0 0 0 18px' }}>
                      {(data.leadStrategy.qualifiesIf || []).map((c, i) => (
                        <li key={i} style={{ fontSize: 12.5, lineHeight: 1.5, color: '#3F5226', marginBottom: 5 }}>{c}</li>
                      ))}
                    </ul>
                  </div>
                  <div style={{ background: PALETTE.dangerSoft, border: '1px solid #E8C9C1', borderRadius: 10, padding: 14 }}>
                    <Eyebrow color={PALETTE.danger} style={{ marginBottom: 8 }}>Disqualifies if</Eyebrow>
                    <ul style={{ margin: 0, padding: '0 0 0 18px' }}>
                      {(data.leadStrategy.disqualifiesIf || []).map((c, i) => (
                        <li key={i} style={{ fontSize: 12.5, lineHeight: 1.5, color: '#7A2A2A', marginBottom: 5 }}>{c}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {data.leadStrategy.qualifierScript && (
                <>
                  <SubTitle>Qualifier script · {data.leadStrategy.qualifierScript.role}</SubTitle>
                  <div style={{ background: PALETTE.surface, border: `1px solid ${PALETTE.line}`, borderRadius: 10, padding: 16, marginBottom: 14 }}>
                    <ol style={{ margin: 0, padding: '0 0 0 20px' }}>
                      {(data.leadStrategy.qualifierScript.questions || []).map((q, i) => (
                        <li key={i} style={{ fontSize: 13, lineHeight: 1.55, color: PALETTE.ink2, marginBottom: 8 }}>{q}</li>
                      ))}
                    </ol>
                  </div>
                  {data.leadStrategy.qualifierScript.threshold && (
                    <div style={{ fontSize: 12, color: PALETTE.ink3, lineHeight: 1.5, fontStyle: 'italic' }}>
                      <strong style={{ fontStyle: 'normal', color: PALETTE.ink2 }}>Threshold:</strong>{' '}
                      {data.leadStrategy.qualifierScript.threshold}
                    </div>
                  )}
                </>
              )}
            </Section>
          )}

          {/* 04 LANDING */}
          {data.landingPage && (
            <Section n="04" id="landing" sectionRefs={sectionRefs}>
              <SectionTitle>Landing page</SectionTitle>
              <div style={{ marginBottom: 22 }}>
                {data.landingPage.slug && <KV label="URL slug" mono>/{data.landingPage.slug}</KV>}
                <KV label="Section count">{lpCount} sections + {data.landingPage.faqs?.length || 0} FAQs</KV>
                <KV label="Form fields">{data.landingPage.formFields?.length || 0} qualification questions</KV>
              </div>

              {data.landingPage.sections?.length > 0 && (
                <>
                  <SubTitle>Page outline</SubTitle>
                  <div style={{ background: PALETTE.surface, border: `1px solid ${PALETTE.line}`, borderRadius: 10, overflow: 'hidden', marginBottom: 22 }}>
                    {data.landingPage.sections.map((s, i, arr) => (
                      <div key={i} className="marketing-brief-outline-row" style={{
                        padding: '13px 16px', borderBottom: i < arr.length - 1 ? `1px solid ${PALETTE.line}` : 'none',
                        display: 'grid', gridTemplateColumns: '100px 1fr 200px', gap: 12, alignItems: 'center',
                      }}>
                        <div style={{ fontSize: 11, color: PALETTE.ink3, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.06em' }}>0{i + 1} · {s.name}</div>
                        <div style={{ fontSize: 13, color: PALETTE.ink, fontWeight: 500, lineHeight: 1.35 }}>{s.headline}</div>
                        <div style={{ fontSize: 11.5, color: PALETTE.accent, textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.cta}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {data.landingPage.formFields?.length > 0 && (
                <>
                  <SubTitle>Qualification form fields</SubTitle>
                  <div style={{ background: PALETTE.surface, border: `1px solid ${PALETTE.line}`, borderRadius: 10, overflow: 'hidden', marginBottom: 22 }}>
                    {data.landingPage.formFields.map((f, i, arr) => (
                      <div key={i} style={{
                        padding: '12px 14px',
                        borderBottom: i < arr.length - 1 ? `1px solid ${PALETTE.line}` : 'none',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                          <div style={{ fontSize: 13, color: PALETTE.ink, fontWeight: 500 }}>{f.field}</div>
                          <Tag tone="soft" size="sm">{f.type}</Tag>
                          {f.options?.length > 0 && (
                            <span style={{ fontSize: 11, color: PALETTE.ink3 }}>{f.options.length} options</span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: PALETTE.ink3, lineHeight: 1.45 }}>{f.purpose}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {data.landingPage.faqs?.length > 0 && (
                <>
                  <SubTitle>FAQ ({data.landingPage.faqs.length})</SubTitle>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 22 }}>
                    {data.landingPage.faqs.map((q, i) => (
                      <details key={i} style={{ background: PALETTE.surface, border: `1px solid ${PALETTE.line}`, borderRadius: 10, padding: '12px 14px' }}>
                        <summary style={{ cursor: 'pointer', fontSize: 13.5, color: PALETTE.ink, fontWeight: 500, listStyle: 'none', display: 'flex', alignItems: 'baseline', gap: 8 }}>
                          <span style={{ color: PALETTE.ink3, fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>Q{i + 1}</span>
                          <span style={{ flex: 1 }}>{q.question}</span>
                        </summary>
                        <div style={{ fontSize: 13, color: PALETTE.ink2, lineHeight: 1.55, marginTop: 8, paddingLeft: 28 }}>{q.answer}</div>
                      </details>
                    ))}
                  </div>
                </>
              )}

              {data.landingPage.autoResponder && (
                <>
                  <SubTitle>Auto-responder copy</SubTitle>
                  <div className="marketing-brief-two-column" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div style={{ background: PALETTE.successSoft, border: '1px solid #CFD9B4', borderRadius: 10, padding: 14 }}>
                      <Eyebrow color={PALETTE.success} style={{ marginBottom: 6 }}>Qualified</Eyebrow>
                      <div style={{ fontSize: 12.5, color: '#3F5226', lineHeight: 1.5 }}>{data.landingPage.autoResponder.qualified}</div>
                    </div>
                    <div style={{ background: PALETTE.surfaceAlt, border: `1px solid ${PALETTE.line}`, borderRadius: 10, padding: 14 }}>
                      <Eyebrow color={PALETTE.ink3} style={{ marginBottom: 6 }}>Disqualified</Eyebrow>
                      <div style={{ fontSize: 12.5, color: PALETTE.ink2, lineHeight: 1.5 }}>{data.landingPage.autoResponder.disqualified}</div>
                    </div>
                  </div>
                </>
              )}
            </Section>
          )}

          {/* 05 ADS */}
          {data.metaAds && (
            <Section n="05" id="ads" sectionRefs={sectionRefs}>
              <SectionTitle>Meta ads</SectionTitle>
              {data.metaAds.objective && <Para>{data.metaAds.objective}</Para>}

              {data.metaAds.creatives?.length > 0 && (
                <>
                  <SubTitle>Creatives ({angleCount})</SubTitle>
                  {data.metaAds.creatives.map((c, i) => (
                    <div key={i} style={{ marginBottom: 14, padding: 16, background: PALETTE.surface, border: `1px solid ${PALETTE.line}`, borderRadius: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                        <span style={{
                          width: 22, height: 22, borderRadius: 5,
                          background: PALETTE.accentTint, color: PALETTE.accentDeep,
                          display: 'grid', placeItems: 'center',
                          fontFamily: 'JetBrains Mono, monospace', fontSize: 11, fontWeight: 600,
                        }}>{i + 1}</span>
                        {c.concept && <div style={{ fontSize: 12, color: PALETTE.ink3, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{c.concept}</div>}
                      </div>
                      <div style={{ fontFamily: 'Instrument Serif, serif', fontSize: 22, color: PALETTE.ink, lineHeight: 1.2, marginBottom: 8, letterSpacing: '-0.01em' }}>{c.headline}</div>
                      <div style={{ fontSize: 13, color: PALETTE.ink2, lineHeight: 1.5, marginBottom: 10 }}>{c.body}</div>
                      {c.visual && (
                        <div style={{ fontSize: 12, color: PALETTE.ink3, lineHeight: 1.5, marginBottom: 10, paddingLeft: 12, borderLeft: `2px solid ${PALETTE.line}`, fontStyle: 'italic' }}>
                          <strong style={{ fontStyle: 'normal', color: PALETTE.ink2 }}>Visual:</strong> {c.visual}
                        </div>
                      )}
                      {c.cta && <div style={{ fontSize: 11, color: PALETTE.accent, letterSpacing: '0.06em', textTransform: 'uppercase' }}>CTA: {c.cta}</div>}
                    </div>
                  ))}
                </>
              )}

              {data.metaAds.audiences?.length > 0 && (
                <>
                  <SubTitle style={{ marginTop: 18 }}>Audiences</SubTitle>
                  {data.metaAds.audiences.map((a, i) => (
                    <div key={i} style={{ background: PALETTE.surface, border: `1px solid ${PALETTE.line}`, borderRadius: 10, padding: 14, marginBottom: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
                        <div style={{ fontSize: 13.5, color: PALETTE.ink, fontWeight: 500 }}>{a.name}</div>
                        {a.ages && <span style={{ fontSize: 11.5, color: PALETTE.ink3, fontFamily: 'JetBrains Mono, monospace' }}>{a.ages} yrs</span>}
                      </div>
                      {a.interests?.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {a.interests.slice(0, 8).map((it, j) => (
                            <span key={j} style={{ fontSize: 10.5, padding: '2px 7px', borderRadius: 999, background: PALETTE.surfaceAlt, color: PALETTE.ink2, border: `1px solid ${PALETTE.line}` }}>{it}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}

              {data.metaAds.formats?.length > 0 && (
                <>
                  <SubTitle style={{ marginTop: 18 }}>Formats</SubTitle>
                  <ul style={{ margin: 0, padding: '0 0 0 20px' }}>
                    {data.metaAds.formats.map((f, i) => <Bullet key={i}>{f}</Bullet>)}
                  </ul>
                </>
              )}
            </Section>
          )}

          {/* 06 EMAIL */}
          {data.email && (
            <Section n="06" id="email" sectionRefs={sectionRefs}>
              <SectionTitle>Email</SectionTitle>
              {data.email.announcement?.length > 0 && (
                <>
                  <SubTitle>Announcement series ({data.email.announcement.length} emails)</SubTitle>
                  <div style={{ marginBottom: 22 }}>
                    {data.email.announcement.map((e, i) => (
                      <EmailRow key={i} e={e} />
                    ))}
                  </div>
                </>
              )}
              {data.email.nurture?.length > 0 && (
                <>
                  <SubTitle>Lead nurture sequence ({data.email.nurture.length} emails)</SubTitle>
                  <div>
                    {data.email.nurture.map((e, i) => (
                      <EmailRow key={i} e={e} />
                    ))}
                  </div>
                </>
              )}
            </Section>
          )}

          {/* 07 METRICS */}
          {data.metrics?.length > 0 && (
            <Section n="07" id="metrics" sectionRefs={sectionRefs}>
              <SectionTitle>Success metrics</SectionTitle>
              <div className="marketing-brief-two-column" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {data.metrics.map((m, i) => (
                  <div key={i} style={{
                    padding: 14, background: PALETTE.surface,
                    border: `1px solid ${PALETTE.line}`, borderRadius: 10,
                  }}>
                    <div style={{
                      fontSize: 11,
                      color: m.type === 'volume' ? PALETTE.accent : PALETTE.success,
                      letterSpacing: '0.08em', textTransform: 'uppercase',
                      marginBottom: 6, fontWeight: 500,
                    }}>{m.type}</div>
                    <div style={{ fontSize: 13.5, color: PALETTE.ink, fontWeight: 500, marginBottom: 4 }}>{m.kpi || m.name}</div>
                    <div style={{ fontSize: 13, color: PALETTE.ink2, fontFamily: 'JetBrains Mono, monospace' }}>{m.target}</div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* 08 DELIVERABLES */}
          {data.deliverables && (
            <Section n="08" id="deliverables" sectionRefs={sectionRefs}>
              <SectionTitle>Deliverables</SectionTitle>
              {Array.isArray(data.deliverables) ? (
                <ul style={{ margin: 0, padding: '0 0 0 20px' }}>
                  {data.deliverables.map((d, i) => <Bullet key={i}>{d}</Bullet>)}
                </ul>
              ) : (
                Object.entries(data.deliverables).map(([key, items]) => (
                  <div key={key} style={{ marginBottom: 22 }}>
                    <SubTitle>{titleCase(key)}</SubTitle>
                    <ul style={{ margin: 0, padding: '0 0 0 20px' }}>
                      {(Array.isArray(items) ? items : []).map((d, i) => <Bullet key={i}>{d}</Bullet>)}
                    </ul>
                  </div>
                ))
              )}
            </Section>
          )}

          {/* 09 VOICE */}
          {data.voice && (
            <Section n="09" id="voice" sectionRefs={sectionRefs}>
              <SectionTitle>Voice & compliance</SectionTitle>
              {data.voice.tone && (
                <div style={{ background: PALETTE.surface, border: `1px solid ${PALETTE.line}`, borderRadius: 10, padding: 16, marginBottom: 16 }}>
                  <SubTitle>Tone</SubTitle>
                  <div style={{ fontSize: 13.5, color: PALETTE.ink2, lineHeight: 1.55 }}>
                    {typeof data.voice.tone === 'string' ? data.voice.tone : (data.voice.tone || []).join(', ')}
                  </div>
                </div>
              )}
              {data.voice.rules?.length > 0 && (
                <>
                  <SubTitle>Voice rules</SubTitle>
                  <ul style={{ margin: 0, padding: '0 0 0 20px', marginBottom: 18 }}>
                    {data.voice.rules.map((r, i) => <Bullet key={i}>{r}</Bullet>)}
                  </ul>
                </>
              )}
              {data.voice.compliance?.length > 0 && (
                <>
                  <SubTitle color={PALETTE.warn}>Compliance flags</SubTitle>
                  <div style={{ background: PALETTE.warnSoft, border: '1px solid #EBDCB2', borderRadius: 10, padding: 14 }}>
                    <ul style={{ margin: 0, padding: '0 0 0 20px' }}>
                      {data.voice.compliance.map((r, i) => (
                        <li key={i} style={{ fontSize: 12.5, lineHeight: 1.5, color: '#7A5A14', marginBottom: 4 }}>{r}</li>
                      ))}
                    </ul>
                  </div>
                </>
              )}
            </Section>
          )}

          {/* 10 ASSUMPTIONS */}
          {data.assumptions?.length > 0 && (
            <Section n="10" id="assumptions" sectionRefs={sectionRefs}>
              <SectionTitle>Assumptions</SectionTitle>
              <Para>Confirm before launch — each of these affects whether the campaign as briefed will actually run.</Para>
              <div style={{ background: PALETTE.surface, border: `1px solid ${PALETTE.line}`, borderRadius: 10, overflow: 'hidden' }}>
                {data.assumptions.map((a, i, arr) => (
                  <div key={i} className="marketing-brief-assumption-row" style={{
                    display: 'grid', gridTemplateColumns: '32px 1fr 90px', gap: 12, padding: '11px 14px',
                    borderBottom: i < arr.length - 1 ? `1px solid ${PALETTE.line}` : 'none',
                    alignItems: 'center',
                  }}>
                    <div style={{ width: 18, height: 18, borderRadius: 4, border: `1.5px solid ${PALETTE.line}` }}/>
                    <div style={{ fontSize: 13, color: PALETTE.ink2, lineHeight: 1.5 }}>
                      {typeof a === 'string' ? a : a.text}
                    </div>
                    <button style={{
                      padding: '4px 8px', fontSize: 11, borderRadius: 5,
                      border: `1px solid ${PALETTE.line}`, background: PALETTE.surfaceAlt, color: PALETTE.ink2,
                      cursor: 'pointer', whiteSpace: 'nowrap',
                    }}>Confirm</button>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── ATOMS ────────────────────────────────────────────────────────────────
function Section({ n, id, sectionRefs, children }) {
  return (
    <section ref={(el) => { sectionRefs.current[id] = el; }} style={{ paddingTop: 44 }}>
      <div data-section-num={n} style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 14 }}>
        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: PALETTE.ink3, letterSpacing: '0.1em' }}>{n}</span>
        {children[0]}
      </div>
      {children.slice(1)}
    </section>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 style={{
      margin: 0, fontFamily: 'Instrument Serif, serif',
      fontSize: 30, fontWeight: 400, color: PALETTE.ink, letterSpacing: '-0.01em',
    }}>{children}</h2>
  );
}

function SubTitle({ children, color, style = {} }) {
  return <Eyebrow style={{ color: color || PALETTE.ink3, marginBottom: 8, ...style }}>{children}</Eyebrow>;
}

function Para({ children }) {
  return <div style={{ fontSize: 14, lineHeight: 1.65, color: PALETTE.ink2, marginBottom: 14 }}>{children}</div>;
}

function Bullet({ children }) {
  return <li style={{ fontSize: 13.5, lineHeight: 1.55, color: PALETTE.ink2, marginBottom: 6 }}>{children}</li>;
}

function KV({ label, children, mono }) {
  return (
    <div className="marketing-brief-kv" style={{
      padding: '9px 0', borderBottom: `1px solid ${PALETTE.line}`,
      display: 'grid', gridTemplateColumns: '150px 1fr', gap: 16, alignItems: 'baseline',
    }}>
      <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: PALETTE.ink3 }}>{label}</div>
      <div style={{
        fontSize: 13.5, color: PALETTE.ink2,
        fontFamily: mono ? 'JetBrains Mono, monospace' : 'Inter, sans-serif',
        lineHeight: 1.5,
      }}>{children}</div>
    </div>
  );
}

const mobileNavButtonStyle = {
  padding: '0 12px',
  borderRadius: 7,
  border: `1px solid ${PALETTE.line}`,
  background: PALETTE.surface,
  color: PALETTE.ink2,
  cursor: 'pointer',
};

const mobileNavPrimaryButtonStyle = {
  ...mobileNavButtonStyle,
  borderColor: PALETTE.ink,
  background: PALETTE.ink,
  color: '#FAF8F5',
};

function EmailRow({ e }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '32px 1fr', gap: 12,
      padding: '10px 0', borderBottom: `1px solid ${PALETTE.line}`,
    }}>
      <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: PALETTE.ink3 }}>#{e.num}</div>
      <div>
        <div style={{ fontSize: 13.5, color: PALETTE.ink, fontWeight: 500, marginBottom: 3 }}>{e.subject}</div>
        <div style={{ fontSize: 12, color: PALETTE.ink3, lineHeight: 1.4 }}>{e.goal || e.preview || ''}</div>
      </div>
    </div>
  );
}

function Eyebrow({ children, color, style = {} }) {
  return (
    <div style={{
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: 10.5, letterSpacing: '0.14em', textTransform: 'uppercase',
      color: color || PALETTE.accent, fontWeight: 500,
      ...style,
    }}>{children}</div>
  );
}

function Tag({ children, tone = 'neutral', size = 'md' }) {
  const tones = {
    neutral: { bg: PALETTE.surfaceAlt, fg: PALETTE.ink2, bd: PALETTE.line },
    accent:  { bg: PALETTE.accentTint, fg: PALETTE.accentDeep, bd: PALETTE.accentSoft },
    soft:    { bg: '#FFF', fg: PALETTE.ink2, bd: PALETTE.line },
  };
  const t = tones[tone];
  const sizes = {
    sm: { padding: '2px 8px',  fontSize: 10.5 },
    md: { padding: '3px 10px', fontSize: 11 },
  };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      ...sizes[size],
      background: t.bg, color: t.fg, border: `1px solid ${t.bd}`,
      borderRadius: 999, fontFamily: 'Inter, sans-serif',
      fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase',
      whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

function titleCase(key) {
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
}
